import axios from "axios";
import { PlayError } from "../utility";
import { toast } from "react-toastify";

const logout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("employee");
  setTimeout(() => {
    window.location.href = "/login";
  }, 1000);
};

//const local = "http://192.168.137.1:5000/api/";
const local = "http://localhost:5000/api/";
const global_legacy = "https://ishopmaster-api-pettah-gsm.onrender.com/api/";
const global = "https://ishopmaster-api-pettah-gsm-fckm.onrender.com/api/";

// [PERF FIX #2] Smart API base URL detection — prevents Mixed Content browser blocks.
// Problem: API was hardcoded to HTTP sslip.io while the UI was served on HTTPS custom domain.
// Chrome/Safari block HTTP requests from HTTPS pages (Mixed Content policy).
const getApiBaseUrl = () => {
  // Running in Node / test environment — no window
  if (typeof window === 'undefined') return local;

  const hostname = window.location.hostname;

  // Custom production HTTPS domain → must use the HTTPS API
  if (hostname === 'izone.ishopmaster.lk') {
    return 'https://api-izone.ishopmaster.lk/api/';
  }

  // sslip.io VPS domain → HTTP VPS API (same-origin HTTP, no mixed-content issue)
  if (hostname.includes('sslip.io')) {
    return 'http://e8w000gwk4s8wk4wowc0gg8o.72.62.247.138.sslip.io/api/';
  }

  // Local development
  return local;
};

const apiService = axios.create({
  baseURL: getApiBaseUrl(),
});

// ✅ Request interceptor
apiService.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Setup passive cold start timer (Render.com free tier)
    // config.metadata = config.metadata || {};
    // config.metadata.coldStartTimer = setTimeout(() => {
    //   toast.info("Server is waking up from sleep mode. Please wait up to 45 seconds...", {
    //     autoClose: 15000,
    //     toastId: "cold-start-warning" // Prevent duplicate toasts
    //   });
    // }, 4000); // 4 seconds delay before assuming cold start

    return config;
  },
  (error) => {
    alert("Token expired, Please re login");
    return Promise.reject(error);
  }
);

// ✅ Helper for API errors
const handleApiError = (error) => {
  const errorResponse = {
    message: "An unexpected error occurred",
    statusCode: 500,
    data: null,
  };

  try {
    errorResponse.message =
      error.response?.data?.message || error.message || error;
    errorResponse.statusCode = error.response?.status || 500;
    errorResponse.data = error.response?.data || null;
  } catch (e) {
    console.error("Error parsing API error:", e);
  }

  return errorResponse;
};

// ✅ Response interceptor
const errorHandlers = {
  "Session expired": () => {
    toast.error("Session expired. Please login again");
    logout();
  },
};

apiService.interceptors.response.use(
  async (response) => {
    if (response.config?.metadata?.coldStartTimer) {
      clearTimeout(response.config.metadata.coldStartTimer);
    }
    return response;
  },
  async (error) => {
    if (error.config?.metadata?.coldStartTimer) {
      clearTimeout(error.config.metadata.coldStartTimer);
    }
    // ✅ Ignore canceled requests (intentional AbortController)
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (typeof error === "string" && errorHandlers[error]) {
      errorHandlers[error]();
      throw new Error(error);
    }

    const { message, statusCode } = handleApiError(error);

    if (message === "Network Error") {
      toast.error("Unable to connect to server. Please check your connection.");
      return Promise.reject(new Error("Network error"));
    }

    switch (statusCode) {
      case 400:
        PlayError();
        toast.error(message || "Invalid request");
        break;
      case 401:
        // Do not logout if the error occurred during a login attempt
        if (error.config?.url?.includes('auth/login')) {
          toast.error(message || "Invalid credentials");
        } else {
          toast.error("Session expired. Please login again");
          logout();
        }
        break;
      case 403:
        if (error.response?.data?.code === 'SOFT_LOCK') {
          // Trigger the global quota request modal
          window.dispatchEvent(new CustomEvent('quota-request-open'));
        } else {
          toast.error(message || "Access denied");
        }

        break;
      case 423: // LOCKED
        if (error.response?.data?.code === 'SOFT_LOCK') {
          // Trigger the global quota request modal
          window.dispatchEvent(new CustomEvent('quota-request-open'));
        } else {
          toast.error(message || "Account is currently locked");
        }
        break;
      case 404:
        toast.error(message || "Resource not found");
        break;
      case 409:
        toast.error(message);
        PlayError();
        break;
      case 500:
        if (message && message !== "An unexpected error occurred") {
          toast.error(message);
          PlayError();
        } else {
          toast.error("Internal server error. Please try again later");
        }
        console.error("Server Error:", {
          url: error.config?.url,
          method: error.config?.method,
          error: message,
        });
        break;
      default:
        toast.error("Something went wrong. Please try again");
    }

    // ❌ Retry logic removed
    return Promise.reject(error);
  }
);

export default apiService;
